import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
  Button,
} from "@heroui/react";
import {
  ContactType,
  ContractContact,
  ContractMilestoneDate,
  ContractPhase,
  MilestoneDateType,
  RentalContract,
} from "shared/types/rental-contracts";
import ColoredDot from "../operationalDashboard/components/coloredDot";
import { UnitDTO } from "shared/types/unit";
import { formatDate } from "shared/utils/get-dates";
import {
  Eye,
  Flag,
  Folder,
  Info,
  ListBullets,
  ProhibitInset,
  SpinnerGap,
  User,
  Calendar,
  Share,
  FileText,
  Cube,
  Car,
} from "@phosphor-icons/react";
import DocumentGeneratorComponent from "components/documents/documentGeneratorComponent";
import { DocumentCategory } from "components/documents/documentGeneratorModal";
import { RenderPrice } from "./renderPrice";
import { useState } from "react";
import MigrationModal from "./migrationModal";
import { OdooContractAction } from "./components/OdooContractAction";
import { useNavigate } from "react-router-dom";
import { TenantContactDetails } from "shared/components/rental/TenantContactDetails";

export const ActiveContractsTable = ({
  company,
  activeContracts,
  rentalInfo,
}: {
  company: any;
  activeContracts: RentalContract[];
  rentalInfo: any;
}) => {
  const [isMigrationModalOpen, setIsMigrationModalOpen] = useState(false);
  const [selectedContract, setSelectedContract] =
    useState<RentalContract | null>(null);

  const navigate = useNavigate();
  const handleOpenMigrationModal = (contract: RentalContract) => {
    setSelectedContract(contract);
    setIsMigrationModalOpen(true);
  };

  const handleCloseMigrationModal = () => {
    setIsMigrationModalOpen(false);
    setSelectedContract(null);
  };

  const handleNavigateToDocumentGenerator = (contract: RentalContract) => {
    // Prepare context entities for the document generator
    const entities: Record<string, any> = {};

    // Add contract entity
    if (contract) {
      entities.contract = {
        type: "contract",
        id: contract.id,
        resourceId: contract.id,
        name: `Contract ${contract.id}`,
        data: contract,
      };
    }

    // Add unit entity if available
    if (contract.unit) {
      entities.unit = {
        type: "unit",
        id: contract.unit.id,
        resourceId: contract.unit.id,
        name: `Unit ${contract.unit.id}`,
        data: contract.unit,
      };
    }

    // Add building entity if available
    if (contract.unit?.building) {
      entities.building = {
        type: "building",
        id: contract.unit.building.id || contract.unit.building.resourceId,
        resourceId:
          contract.unit.building.resourceId || contract.unit.building.id,
        name:
          contract.unit.building.googlePlaceLongAddress ||
          `Building ${contract.unit.building.id}`,
        data: contract.unit.building,
      };
    }

    // Add all tenant contacts - use resource instead of contact
    const tenants =
      contract.contractContacts?.filter(
        (cc: ContractContact) => cc.contactType === ContactType.TENANT,
      ) || [];

    tenants.forEach((tenant, index) => {
      if (tenant?.resource) {
        const contactKey = index === 0 ? "contact" : `contact${index + 1}`;
        entities[contactKey] = {
          type: "contact",
          id: tenant.resource.id,
          resourceId: tenant.resource.id,
          name: tenant.resource.name || "Contact",
          data: tenant.resource,
        };
      }
    });

    // Prepare dynamic values
    const dynamicValues: Record<string, string> = {};

    // Amount of tenants
    const tenantCount =
      contract.contractContacts?.filter(
        (cc: ContractContact) => cc.contactType === ContactType.TENANT,
      ).length || 0;
    dynamicValues.amount_of_tenants = tenantCount.toString();

    // Start date
    const startDate = contract.contractMilestoneDates?.find(
      (date: ContractMilestoneDate) =>
        date.milestoneDateType === MilestoneDateType.START_DATE,
    );
    if (startDate?.date) {
      dynamicValues.start_date = formatDate(new Date(startDate.date));
    }

    // End date
    const endDate = contract.contractMilestoneDates?.find(
      (date: ContractMilestoneDate) =>
        date.milestoneDateType === MilestoneDateType.END_DATE,
    );
    if (endDate?.date) {
      dynamicValues.end_date = formatDate(new Date(endDate.date));
    }

    // Monthly rent
    if (contract.activePrice?.price) {
      dynamicValues.monthly_rent = `€${contract.activePrice.price.toFixed(2)}`;
    }

    // Navigate to document generator with prefilled context
    navigate("/document-generator", {
      state: {
        entities,
        category: "CONTRACT",
        dynamicValues,
      },
    });
  };

  return (
    <>
      <Table
        aria-label="Example static collection table"
        classNames={{
          wrapper: "shadow-none rounded-none p-0",
          thead: "h-fit border-b-2 border-neutral-primary/40",
          th: "rounded-none! bg-white py-2 h-fit text-sm text-neutral-primary font-medium",
          tbody: "border-b divide-y divide-neutral-primary/15",
          tr: "divide-x divide-neutral-primary/15",
          td: "py-2.5",
        }}
      >
        <TableHeader>
          <TableColumn>Acties</TableColumn>
          <TableColumn>Documentgenerator</TableColumn>
          <TableColumn className="min-w-[180px]">Unit</TableColumn>
          <TableColumn>Due diligence</TableColumn>
          <TableColumn>Eigenaar</TableColumn>
          <TableColumn>Huurder</TableColumn>
          <TableColumn>Contactgegevens</TableColumn>
          <TableColumn>Fase</TableColumn>
          <TableColumn>Startdatum</TableColumn>
          <TableColumn>Juridische vorm</TableColumn>
          <TableColumn>Aantal bewoners</TableColumn>
          <TableColumn>Openstaand</TableColumn>
          <TableColumn>Geïndexeerde huurprijs</TableColumn>
          <TableColumn>Provisies</TableColumn>
          <TableColumn>OV</TableColumn>
          <TableColumn>Gebouw attributen</TableColumn>
          <TableColumn>Facturatiefrequentie</TableColumn>
          <TableColumn>Huurdersportaal Migratie</TableColumn>
        </TableHeader>
        <TableBody>
          {activeContracts &&
            activeContracts.map((contract, index) => (
              <TableRow key={index}>
                <TableCell>
                  <div className="flex cursor-pointer items-center gap-3">
                    <Tooltip content="Details bekijken">
                      <a
                        className="text-moss-primary"
                        onClick={() =>
                          rentalInfo.handleOpenViewContractModal(contract)
                        }
                      >
                        <Eye weight="duotone" size={22} />
                      </a>
                    </Tooltip>
                    <OdooContractAction
                      contract={contract as any}
                      onSynced={rentalInfo.fetchRentalContracts}
                    />
                    <Tooltip content="Contractproducten bewerken">
                      <a
                        className="text-moss-primary cursor-pointer"
                        onClick={() =>
                          rentalInfo.handleOpenEditProductsModal(contract)
                        }
                      >
                        <ListBullets weight="duotone" size={22} />
                      </a>
                    </Tooltip>
                    <Tooltip
                      content={
                        contract?.flaggedForWatchlist ? (
                          <div className="max-w-[200px]">
                            <p className="mb-1">
                              {contract.flaggedForWatchlistReason}
                            </p>
                            <p className="text-neutral-secondary text-sm">
                              Klik om van de watchlist te verwijderen
                            </p>
                          </div>
                        ) : (
                          "Op watchlist zetten"
                        )
                      }
                    >
                      <Flag
                        className={`${contract?.flaggedForWatchlist ? "text-brick-primary" : ""}`}
                        weight="duotone"
                        size={22}
                        onClick={() =>
                          rentalInfo.handleFlagForWatchList(contract)
                        }
                      />
                    </Tooltip>
                    {contract?.phase == ContractPhase.ACTIVE ? (
                      <Tooltip content="Contract opzeggen">
                        <a
                          className="text-status-bad"
                          onClick={() =>
                            rentalInfo.handleTerminateContract(contract)
                          }
                        >
                          <ProhibitInset weight="duotone" size={22} />
                        </a>
                      </Tooltip>
                    ) : (
                      <Tooltip content="Contract in opzeg">
                        <a
                          className="text-status-bad"
                          onClick={() =>
                            rentalInfo.handleTerminateContractAbruptly(contract)
                          }
                        >
                          <SpinnerGap weight="duotone" size={22} />
                        </a>
                      </Tooltip>
                    )}
                    {contract?.unit && (
                      <Tooltip content="Open unitfolder op SharePoint">
                        <a
                          href={contract?.unit?.sharePointFolderUrl}
                          target="_blank"
                          className="border-neutral-primary/20 text-neutral-primary flex h-8 w-8 items-center justify-center rounded-full border"
                        >
                          <Folder />
                        </a>
                      </Tooltip>
                    )}
                    {rentalInfo.hasUpcomingPriceChanges(contract) && (
                      <Tooltip content="Contract heeft aankomende prijswijzigingen">
                        <Calendar
                          weight="duotone"
                          size={20}
                          className="text-warning"
                        />
                      </Tooltip>
                    )}
                    {rentalInfo.isNearIndexation(contract) && (
                      <Tooltip content="Contract nadert indexatiedatum">
                        <Calendar
                          weight="duotone"
                          size={20}
                          className="text-brick-primary"
                        />
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <DocumentGeneratorComponent
                    incomingContract={contract as RentalContract}
                    incomingUnit={contract.unit as UnitDTO}
                    incomingDocumentCategory={DocumentCategory.CONTRACT}
                    size="sm"
                  />
                  <Tooltip content="Open document generator met vooringevulde context">
                    <Button
                      size="sm"
                      variant="flat"
                      color="primary"
                      isIconOnly
                      onPress={() =>
                        handleNavigateToDocumentGenerator(contract)
                      }
                    >
                      <FileText size={18} weight="duotone" />
                    </Button>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {(() => {
                    // Work out what the contract actually contains so we can show the right icon(s):
                    // a unit icon when there's a unit, a car icon when there are building attributes,
                    // both when the contract bundles a unit + attribute(s).
                    const hasUnit = !!contract?.unit?.id;
                    const attributeNames = (contract?.products || [])
                      .map((p: any) => p?.buildingAttribute?.name)
                      .filter(Boolean);
                    const hasAttribute = attributeNames.length > 0;

                    return (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 shrink-0">
                          {hasUnit && (
                            <Tooltip content="Bevat een unit">
                              <Cube weight="duotone" size={18} className="text-moss-primary" />
                            </Tooltip>
                          )}
                          {hasAttribute && (
                            <Tooltip
                              content={`Bevat gebouwattribuut: ${attributeNames.join(", ")}`}
                            >
                              <Car weight="duotone" size={18} className="text-brick-primary" />
                            </Tooltip>
                          )}
                        </div>

                        {hasUnit ? (
                          <a
                            href={`/unit/${contract?.unit?.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-moss-primary hover:text-moss-primary/80 font-medium transition-colors"
                          >
                            {`${contract?.unit?.id} ${contract?.unit?.referencePlus ? `(${contract?.unit?.referencePlus})` : ""}`}
                          </a>
                        ) : (
                          // Attribute-only contract (e.g. standalone parking): no unit, show the attribute(s).
                          <span className="font-medium text-neutral-primary">
                            {hasAttribute ? attributeNames.join(", ") : "Gebouwattribuut"}
                          </span>
                        )}
                        <Tooltip
                          content={("Database contract id: " + contract.id) as any}
                        >
                          <Info weight="duotone" size={18} />
                        </Tooltip>
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <div className="flex cursor-pointer items-center justify-center rounded-lg">
                    <ColoredDot
                      color="green"
                      number={
                        (contract.operationalChecks &&
                          (contract.operationalChecks as any).green.count) ||
                        0
                      }
                      labels={
                        (contract.operationalChecks &&
                          (contract.operationalChecks as any).green.labels) ||
                        []
                      }
                    />
                    <ColoredDot
                      color="orange"
                      number={
                        (contract.operationalChecks &&
                          (contract.operationalChecks as any).orange.count) ||
                        0
                      }
                      labels={
                        (contract.operationalChecks &&
                          (contract.operationalChecks as any).orange.labels) ||
                        []
                      }
                    />
                    <ColoredDot
                      color="red"
                      number={
                        (contract.operationalChecks &&
                          (contract.operationalChecks as any).red.count) ||
                        0
                      }
                      labels={
                        (contract.operationalChecks &&
                          (contract.operationalChecks as any).red.labels) ||
                        []
                      }
                    />
                    <ColoredDot
                      color="transparent"
                      number={
                        (contract.operationalChecks &&
                          (contract.operationalChecks as any).transparent
                            .count) ||
                        0
                      }
                      labels={
                        (contract.operationalChecks &&
                          (contract.operationalChecks as any).transparent
                            .labels) ||
                        []
                      }
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-neutral-primary text-sm">
                    {company?.name || ""}
                    {company?.legalForm ? ` ${company?.legalForm}` : ""}
                  </span>
                </TableCell>
                <TableCell>
                  <Chip
                    className="gap-1"
                    startContent={
                      <User
                        weight="duotone"
                        size={14}
                        className={`${
                          contract?.phase == ContractPhase.TERMINATION_PHASE
                            ? "text-brick-primary"
                            : "text-moss-primary"
                        }`}
                      />
                    }
                    classNames={{
                      base: `h-fit bg-white px-2 border ${
                        contract?.phase == ContractPhase.TERMINATION_PHASE
                          ? "border-brick-primary text-brick-primary"
                          : "border-moss-primary text-moss-primary"
                      }`,
                      content: "px-0 font-medium",
                    }}
                  >
                    {contract.contractContacts &&
                      contract.contractContacts
                        .filter(
                          (contact: ContractContact) =>
                            contact.contactType == ContactType.TENANT,
                        )
                        .map((contact: any) => contact.resource?.name)
                        .join(", ")}
                  </Chip>
                </TableCell>
                <TableCell>
                  <TenantContactDetails
                    contacts={(contract.contractContacts ?? []).filter(
                      (contact: ContractContact) =>
                        contact.contactType == ContactType.TENANT,
                    )}
                  />
                </TableCell>
                <TableCell>{contract.phase}</TableCell>
                <TableCell>
                  {contract.contractMilestoneDates &&
                    contract.contractMilestoneDates.filter(
                      (milestone: ContractMilestoneDate) =>
                        milestone.milestoneDateType ==
                        MilestoneDateType.START_DATE,
                    ) &&
                    formatDate(
                      new Date(
                        (contract.contractMilestoneDates as any).filter(
                          (milestone: ContractMilestoneDate) =>
                            milestone.milestoneDateType ==
                            MilestoneDateType.START_DATE,
                        )[0]?.date,
                      ),
                    )}
                </TableCell>
                <TableCell>{contract.legalForm}</TableCell>
                <TableCell>{contract.numResidents}</TableCell>
                <TableCell className="font-medium">Aan te vullen</TableCell>
                <TableCell>
                  <RenderPrice
                    price={contract.activePrice}
                    label="Huurprijs"
                    contract={contract}
                    rentalInfo={rentalInfo}
                  />
                </TableCell>
                <TableCell>
                  <RenderPrice
                    price={contract.activeProvision}
                    label="Provisie"
                    contract={contract}
                    rentalInfo={rentalInfo}
                  />
                </TableCell>
                <TableCell>
                  <RenderPrice
                    price={contract.activePropertyTaxes}
                    label="Onroerende voorheffing"
                    contract={contract}
                    rentalInfo={rentalInfo}
                  />
                </TableCell>
                <TableCell>
                  {rentalInfo.renderBuildingAttributeRevenue(contract)}
                </TableCell>
                <TableCell>{contract.invoicingFrequency}</TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    color="success"
                    variant="flat"
                    startContent={<Share size={16} />}
                    onPress={() => handleOpenMigrationModal(contract)}
                  >
                    Migratie
                  </Button>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>

      <MigrationModal
        isOpen={isMigrationModalOpen}
        onClose={handleCloseMigrationModal}
        selectedContract={selectedContract}
      />
    </>
  );
};
